
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSidebar } from "@/components/ui/sidebar";
import { menuItems } from "@/components/sidebar/menuItems";

export function useSidebarState() {
  const location = useLocation();
  const { state } = useSidebar();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [showIcons, setShowIcons] = useState(true);
  const [orderedMenuItems, setOrderedMenuItems] = useState(menuItems);

  const isCollapsed = state === "collapsed";

  useEffect(() => {
    console.log('Current location:', location.pathname);
    console.log('Menu items count:', menuItems.length);
    
    let customizedItems = [...menuItems];
    
    // Load menu customization settings from MenuReorderSection
    const savedOrder = localStorage.getItem('menu-order');
    const savedVisibility = localStorage.getItem('menu-visibility');
    const savedTitles = localStorage.getItem('menu-titles');
    
    // Apply custom order if exists
    if (savedOrder) {
      try {
        const orderArray = JSON.parse(savedOrder);
        const orderedItems = orderArray.map((id: string) => {
          const index = parseInt(id.replace('menu-', ''));
          return menuItems[index];
        }).filter(Boolean);
        if (orderedItems.length > 0) {
          customizedItems = orderedItems;
        }
      } catch (e) {
        console.error('Error parsing saved order:', e);
      }
    }
    
    // Apply custom titles if exists
    if (savedTitles) {
      try {
        const titlesMap = JSON.parse(savedTitles);
        customizedItems = customizedItems.map((item, index) => {
          const id = `menu-${menuItems.indexOf(item)}`;
          return titlesMap[id] ? { ...item, title: titlesMap[id] } : item;
        });
      } catch (e) {
        console.error('Error parsing saved titles:', e);
      }
    }
    
    // Apply visibility settings if exists
    if (savedVisibility) {
      try {
        const visibilityMap = JSON.parse(savedVisibility);
        customizedItems = customizedItems.filter((item, index) => {
          const id = `menu-${menuItems.indexOf(item)}`;
          return visibilityMap[id] !== false;
        });
      } catch (e) {
        console.error('Error parsing saved visibility:', e);
      }
    }
    
    // Load basic settings
    const savedShowIcons = localStorage.getItem('sidebar-show-icons');
    const savedIconSize = localStorage.getItem('sidebar-icon-size');
    const savedFontScale = localStorage.getItem('sidebar-font-scale');
    const savedMenuSpacing = localStorage.getItem('menu-spacing');

    if (savedShowIcons) {
      setShowIcons(savedShowIcons === 'true');
    }

    // Apply saved settings to CSS variables
    if (savedIconSize) {
      document.documentElement.style.setProperty('--sidebar-icon-size', `${savedIconSize}rem`);
    }
    
    if (savedFontScale) {
      document.documentElement.style.setProperty('--sidebar-font-scale', savedFontScale);
    }
    
    if (savedMenuSpacing) {
      document.documentElement.style.setProperty('--menu-spacing', `${savedMenuSpacing}rem`);
    }

    console.log('Final menu items count:', customizedItems.length);
    setOrderedMenuItems(customizedItems);

    // Function to find item in nested structure
    const findItemByPath = (items: any[], path: string): any => {
      for (const item of items) {
        if (item.href === path || item.url === path) {
          return item;
        }
        if (item.subItems) {
          const found = findItemByPath(item.subItems, path);
          if (found) {
            return { item: found, parent: item };
          }
        }
      }
      return null;
    };

    // Auto-expand current section based on current path
    const result = findItemByPath(customizedItems, location.pathname);
    
    if (result && !isCollapsed) {
      const itemsToExpand = [];
      
      if (result.parent) {
        console.log('Auto-expanding parent:', result.parent.title);
        itemsToExpand.push(result.parent.title);
        
        // If there's a grandparent, expand it too
        const grandParentResult = customizedItems.find(item => 
          item.subItems?.some((subItem: any) => subItem.title === result.parent.title)
        );
        if (grandParentResult) {
          console.log('Auto-expanding grandparent:', grandParentResult.title);
          itemsToExpand.push(grandParentResult.title);
        }
      } else if (result.item?.subItems) {
        console.log('Auto-expanding current item:', result.item.title);
        itemsToExpand.push(result.item.title);
      }
      
      setExpandedItems(prev => {
        const newExpanded = [...prev];
        itemsToExpand.forEach(title => {
          if (!newExpanded.includes(title)) {
            newExpanded.push(title);
          }
        });
        return newExpanded;
      });
    }
  }, [location.pathname, isCollapsed]);

  const toggleExpanded = (title: string) => {
    if (!isCollapsed) {
      setExpandedItems(prev => 
        prev.includes(title) 
          ? prev.filter(item => item !== title)
          : [...prev, title]
      );
    }
  };

  const isActive = (href: string) => {
    const active = location.pathname === href;
    if (active) {
      console.log(`Active route found: ${href}`);
    }
    return active;
  };

  const isParentActive = (item: any) => {
    const checkSubItems = (subItems: any[]): boolean => {
      return subItems.some((subItem: any) => {
        if (isActive(subItem.href)) return true;
        if (subItem.subItems) return checkSubItems(subItem.subItems);
        return false;
      });
    };

    const hasActiveChild = item.subItems ? checkSubItems(item.subItems) : false;
    if (hasActiveChild) {
      console.log(`Parent ${item.title} has active child`);
    }
    return hasActiveChild;
  };

  return {
    expandedItems,
    showIcons,
    orderedMenuItems,
    isCollapsed,
    toggleExpanded,
    isActive,
    isParentActive
  };
}
